import { ulid } from "ulid"
import { createActionUtils, IAction, IActionJavaData, IActionWebConfig } from "@droid/project-v2-interface"
import { appActionNamespace } from "../app-namespace"
import { IActionOpenAppUI } from "./IActionOpenApp.UI"
import { ManifestChinesePhoneSpecialPermission, ManifestPermission } from "@droid/android"
import { RhinoInjectNames } from "@app/static"
import { getI18nT, loadI18nValue, Log } from '@fanfanlo'
import { Namespace } from "i18next"
import { IPermissionData } from '@droid/project-v2-interface'

const fileLog = new Log(false, '[IActionOpenApp]')
fileLog.pause = true
fileLog.childrenPaused = true

const actionType = `${appActionNamespace}openApp`
fileLog.log('[IActionOpenApp] type:', actionType)

const i18nNamespace = 'droid-project-v2-ext/actions/app/action-open-app/content'

export interface IPermissionI18nData {
    reasonI18nNs: string
    reasonI18nKey: string
}

export interface IActionOpenAppJavaData extends IActionJavaData {
    packageName: string
}

export interface IActionOpenAppWebConfig extends IActionWebConfig {
    permissionsMap: Record<string, IPermissionI18nData>
}

export interface IActionOpenAppData extends IAction<IActionOpenAppJavaData, IActionOpenAppWebConfig> {
    javaData: IActionOpenAppJavaData
}

function createDefaultData(): IActionOpenAppData {
    fileLog.log('[createDefaultData] 开始创建默认数据')

    const ulidValue = ulid()
    fileLog.log('[createDefaultData] 生成ulid:', ulidValue)

    const permissions: IPermissionData[] = [
        {
            name: '辅助功能权限',
            value: ManifestPermission.BIND_ACCESSIBILITY_SERVICE,
            reason: '需要辅助功能来检测应用是否已打开'
        },
        {
            name: '后台弹出窗口权限',
            value: ManifestChinesePhoneSpecialPermission.BACKGROUND_POPUP,
            reason: '需要后台弹出权限来在后台显示弹窗通知'
        }
    ]
    const permissionsMap = {
        [ManifestPermission.BIND_ACCESSIBILITY_SERVICE]: {
            reasonI18nNs: i18nNamespace,
            reasonI18nKey: ManifestPermission.BIND_ACCESSIBILITY_SERVICE
        },
        [ManifestChinesePhoneSpecialPermission.BACKGROUND_POPUP]: {
            reasonI18nNs: i18nNamespace,
            reasonI18nKey: ManifestChinesePhoneSpecialPermission.BACKGROUND_POPUP
        },
    }
    fileLog.log('[createDefaultData] 权限数量:', permissions.length)

    const injects: ([typeof RhinoInjectNames.a11yModel, string])[] = [
        [RhinoInjectNames.a11yModel, 'a11yModel']
    ]
    fileLog.log('[createDefaultData] 注入配置数量:', injects.length)

    fileLog.log('[createDefaultData] permissionsMap=', JSON.stringify(permissionsMap))
    const defaultData = {
        
        ulid: ulidValue,
        uuid: '',
        ownerUuid: '',
        currentVersion: 0,
        type: actionType,
        name: 'actionOpenApp',
        permissions,
        injects,
        javaData: {
            packageName: ''
        },
        webConfig: {
            permissionsMap
        },
        createdAt: 0,
        updatedAt: 0,
        authorUuid: '',
    }
    fileLog.log('[createDefaultData] defaultData:', JSON.stringify(defaultData))
    return defaultData
}

function init() {
    fileLog.log('[init] actionOpenApp 初始化')
}

// 添加异步加载国际化文本的函数
async function loadI18nTexts(data: IActionOpenAppData): Promise<void> {
    fileLog.log('[loadI18nTexts] 开始加载国际化文本')
    fileLog.log('[loadI18nTexts] data:', JSON.stringify(data))

    if (!data.permissions) {
        fileLog.log('[loadI18nTexts] data.permissions 为 undefined, 跳过加载')
        return
    }
    if (data.permissions.length === 0) {
        fileLog.log('[loadI18nTexts] data.permissions 长度为 0, 跳过加载')
        return
    }

    // 加载 IActionOpenApp 的国际化文本 (权限原因)
    fileLog.log('[loadI18nTexts] 加载 IActionOpenApp 国际化命名空间')
    const actionT = await getI18nT('droid-project-v2-ext/actions/app/action-open-app/content')
    fileLog.log('[loadI18nTexts] IActionOpenApp 国际化加载完成')

    // 更新权限数据
    const permissionsLength = data.permissions.length
    fileLog.log('[loadI18nTexts] 权限数量:', permissionsLength)
    for (let i = 0; i < permissionsLength; i++) {
        const permission = data.permissions[i]
        fileLog.log('[loadI18nTexts] 处理权限索引:', i, 'value:', permission.value)

        const permissionI18n = data.webConfig.permissionsMap[permission.value]
        fileLog.log('[loadI18nTexts] permissionI18n=', JSON.stringify(permissionI18n))
        if (permissionI18n) {
            const ns = permissionI18n.reasonI18nNs
            const key = permissionI18n.reasonI18nKey
            fileLog.log('[loadI18nTexts] permission i18n ns key', ns, key)
            if (ns && key) {
                const fullKey = `IActionOpenApp.permission.${key}`
                const reason = await loadI18nValue(ns as Namespace, fullKey)
                if (reason != fullKey) {
                    permission.reason = reason
                    fileLog.log('[loadI18nTexts] i18n找到了值', permission.reason)
                } else {
                    fileLog.log('[loadI18nTexts] i18n没有找到值，key=', fullKey)
                }
            }
        }
    }

    fileLog.log('[loadI18nTexts] 所有权限国际化文本加载完成')
}

export const actionOpenAppUtils = createActionUtils(actionType, init, createDefaultData, IActionOpenAppUI)

// 添加 loadI18nTexts 方法
actionOpenAppUtils.loadI18nTexts = loadI18nTexts
